import type { Action } from '@unifyapps/defs/types/action';
import type { BlockId } from '@unifyapps/defs/types/block';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import _find from 'lodash/find';
import { useContext } from 'react';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import { useGetDependencyGraphProvider } from '../../../../context/DependencyGraphProvider';
import { useRegistryContext } from '../../../../context/RegistryProvider';
import InterfacePageHelper from '../../../../helper/InterfacePage';
import { isPulseOn, pulse } from '../../../../pulse/floor';
import { scopeIdOf } from '../../../../pulse/scopeRegistry';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import { useGetGlobalStateStoreStateByInstanceId } from '../../../../stores/GlobalStateStore';
import { GlobalStateStoreManagerContext } from '../../../../stores/GlobalStateStore/GlobalStateStoreProvider';
import { useGetInterfaceStoreStateByInstanceId } from '../../../../stores/InterfaceStore';
import { resolveBlockState } from '../../../../utils/repeatables/blockStateResolver';
import { getOriginalBlockId, isRuntimeBlockId } from '../../../../utils/runTimeBlocks';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { useComputeConditionEvaluationGetter } from '../../../useComputeConditionEvaluationGetter';
import { SHOULD_NOT_PROPAGATE_UP, SHOULD_PROPAGATE_UP } from '../../const';
import { useExportBlock } from '../exportBlock';

type ControlComponentPayloadType = {
  blockId: string;
  methodName: string;
  methodPayload: Record<string, unknown>;
};

function useControlBlockMethod() {
  const { showSnackbar, closeSnackbar } = useSnackbar();
  const { registry } = useRegistryContext();
  const getGlobalStateStoreState = useGetGlobalStateStoreStateByInstanceId();
  const getInterfaceStoreState = useGetInterfaceStoreStateByInstanceId();
  const getComputedFilters = useComputeConditionEvaluationGetter();
  const applicationType = useGlobalAppStateStore().hooks.record.useApplicationType();
  const { getDependencyGraphProvider } = useGetDependencyGraphProvider();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const { onExportBlock } = useExportBlock();
  const managerId = useContext(GlobalStateStoreManagerContext)?.managerId ?? 'unknown';

  const onCallBlockMethod = useEventCallback(
    async ({
      blockId: _blockId,
      actionContext,
      action,
      methodName,
      targetBlockId,
      anchorId,
      instanceId,
    }: {
      /**
       * this comes from the action payload, which is non-runtime block id because we pick it in dropdown of blocks when configuring action in the Builder
       */
      blockId: string;
      methodName: string;
      action: Action;
      actionContext: OnActionArgs['actionContext'];
      /**
       * targetBlockId is the blockId of the block from which this method is called,
       * targetBlockId can look like runTimeBlockKey_b_1234 or b_1234
       */
      targetBlockId?: BlockId;
      anchorId?: string;
      /** page instance ID from action payload */
      instanceId?: string;
    }) => {
      const {
        blocks: globalStateBlocks,
        blockRefs,
        actions: globalStateActions,
      } = getGlobalStateStoreState(instanceId);

      const { activeInterfacePageId, interfacePages } = getInterfaceStoreState(instanceId);

      const blocks = InterfacePageHelper.getBaseDeviceBlocks(interfacePages[activeInterfacePageId]);

      const computedAction = getNoCodeComputedData<Action>(
        action as unknown as Record<string, unknown>,
        actionContext,
      );

      // Row-scope auto-resolution: when an action picks a block by static id
      // and fires from inside a Repeatable row, attachRowScopeIdentities has
      // already rewritten actionContext[blockId].id to the row's runtime
      // instance id, so reading from there auto-targets the right row.
      // Skip auto-resolution if the caller already passed a runtime id —
      // they meant exactly that.
      const blockId = getNoCodeComputedData<string>(_blockId, actionContext);

      const isUserOverride = isRuntimeBlockId(blockId);

      const contextEntry = actionContext?.[blockId] as { id?: string } | undefined;
      const autoResolvedId =
        !isUserOverride &&
        contextEntry?.id &&
        isRuntimeBlockId(contextEntry.id) &&
        getOriginalBlockId(contextEntry.id) === blockId
          ? contextEntry.id
          : blockId;

      const originalBlockId = isRuntimeBlockId(autoResolvedId)
        ? getOriginalBlockId(autoResolvedId)
        : autoResolvedId;

      // get the original looking blockId, like b_oko1, either from targetBlockId, which could be runtime or non-runtime
      // or from blockId which is always non-runtime
      const shouldUseTargetBlockId =
        targetBlockId &&
        isRuntimeBlockId(targetBlockId) &&
        getOriginalBlockId(targetBlockId) === blockId;

      // if targetBlockId is present, then there must be block state against it
      const stateBlockId =
        !isRuntimeBlockId(autoResolvedId) && shouldUseTargetBlockId
          ? targetBlockId
          : autoResolvedId;

      const block = _find(blocks, { id: originalBlockId });

      if (!block) {
        // if the block is not found, returning true to propagate the event to the parent
        return Promise.resolve(SHOULD_PROPAGATE_UP);
      }

      const controlMethods = registry.getBlockControlMethods(
        block.component.componentType,
        block,
        applicationType,
      );
      const method = _find(controlMethods, { id: methodName })?.method;

      if (method) {
        // resolveBlockState synthesizes state for untouched repeatable instances
        // from the parent template; without it, methods targeting an unwritten
        // instance would receive an undefined state argument.
        const blockState = resolveBlockState(
          stateBlockId,
          globalStateBlocks,
          (originalId) => globalStateBlocks[originalId],
        );
        const nextBlockState = await method(
          blockState,
          computedAction,
          blockRefs[stateBlockId]?.current,
          {
            showSnackbar,
            closeSnackbar,
            getComputeContext: () => actionContext,
            getComputedFilters,
            getComputedData: getNoCodeComputedData,
            ...(methodName === 'exportBlock' ? { onExportBlock } : {}),
            targetBlockId,
            anchorId,
            block,
            registry,
          },
        );
        if (isPulseOn()) {
          pulse.componentMethod(
            scopeIdOf(managerId, instanceId ?? null),
            methodName,
            { type: 'block', id: stateBlockId },
            computedAction.payload?.methodPayload,
          );
        }
        if (nextBlockState) {
          globalStateActions.setBlockState(stateBlockId, nextBlockState);
          const {
            actions: { notifyDependencyChange },
          } = getDependencyGraphProvider(instanceId);
          notifyDependencyChange({
            id: originalBlockId,
          });
        }

        // we have handled the block control method, so returning false to prevent the event from propagating to the parent
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
  );

  const onControlBlockMethod: ActionHandlerType = useEventCallback(
    ({ action, actionContext, blockId, anchorId, instanceId }: OnActionArgs) => {
      const payload = action.payload as ControlComponentPayloadType;

      return onCallBlockMethod({
        blockId: payload.blockId,
        targetBlockId: blockId,
        anchorId,
        methodName: payload.methodName,
        actionContext,
        action,
        instanceId,
      });
    },
  );

  return { onControlBlockMethod, onCallBlockMethod };
}

export default useControlBlockMethod;
